import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { 
  Settings, 
  User, 
  LogOut, 
  Home, 
  Search, 
  Briefcase,
  Menu,
  X,
  MapPin,
  Plus
} from 'lucide-react';
import { UserProfile } from '@/types/user';

interface NavbarProps {
  onLogout?: () => void;
  onCityChange?: (city: string) => void;
}

interface UserCity {
  id: string;
  city: string;
}

export function Navbar({ onLogout, onCityChange }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userCities, setUserCities] = useState<UserCity[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const loadUserProfiles = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/');
        return;
      }

      try {
        const userProfilesRef = collection(db, 'userProfiles');
        const q = query(userProfilesRef, where('userId', '==', user.uid));
        const querySnapshot = await getDocs(q);
        
        const cities = querySnapshot.docs.map(doc => ({
          id: doc.id,
          city: doc.data().city
        }));
        
        setUserCities(cities);

        // Get current city from localStorage
        const currentCity = localStorage.getItem('currentCity');
        
        if (currentCity && cities.some(c => c.city === currentCity)) {
          setSelectedCity(currentCity);
        } else if (cities.length > 0) {
          // Set first city as default if no city is selected
          setSelectedCity(cities[0].city);
          localStorage.setItem('currentCity', cities[0].city);
        }
      } catch (error) {
        console.error('Error loading user profiles:', error);
      }
    };

    loadUserProfiles();
  }, [navigate]);

  const handleCityChange = async (city: string) => {
    try {
      localStorage.setItem('currentCity', city);
      setSelectedCity(city);
      
      if (onCityChange) {
        onCityChange(city);
      }
    } catch (error) {
      console.error('Error changing city:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('currentCity');
      if (onLogout) {
        onLogout();
      }
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  const isActive = (path: string) => location.pathname === path;

  const NavLinks = () => (
    <>
      <Button
        asChild
        variant={isActive('/dashboard') ? "default" : "ghost"}
        className="w-full md:w-auto justify-start md:justify-center"
      >
        <Link to="/dashboard" className="flex items-center gap-2">
          <Home className="h-4 w-4" />
          <span>Início</span>
        </Link>
      </Button>
      
      <Button
        asChild
        variant={isActive('/especialistas') ? "default" : "ghost"}
        className="w-full md:w-auto justify-start md:justify-center"
      >
        <Link to="/especialistas" className="flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          <span>Procurar Especialista</span>
        </Link>
      </Button>
      
      <Button
        asChild
        variant={isActive('/servicos') ? "default" : "ghost"}
        className="w-full md:w-auto justify-start md:justify-center"
      >
        <Link to="/servicos" className="flex items-center gap-2">
          <Search className="h-4 w-4" />
          <span>Procurar Serviço</span>
        </Link>
      </Button>
    </>
  );
  
  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <img src="/logo.svg" alt="ConectaComércio" className="h-8" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <NavLinks />
            
            {/* City Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{selectedCity || 'Selecionar cidade'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {userCities.map((city) => (
                  <DropdownMenuItem
                    key={city.id}
                    onClick={() => handleCityChange(city.city)}
                    className="flex items-center gap-2"
                  >
                    <MapPin className="h-4 w-4" />
                    <span>{city.city}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/city-selection')}
                  className="flex items-center gap-2 text-blue-600"
                >
                  <Plus className="h-4 w-4" />
                  <span>Adicionar nova cidade</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon">
              <Settings className="h-5 w-5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <div className="bg-blue-600 w-full h-full flex items-center justify-center text-white">
                      {profile?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Meu Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <div className="bg-blue-600 w-full h-full flex items-center justify-center text-white">
                      {profile?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Meu Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            <NavLinks />
            
            {/* Mobile City Selector */}
            <div className="px-2 py-1">
              <p className="text-sm font-medium text-gray-500 mb-2">Cidade atual</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full flex items-center gap-2 justify-start">
                    <MapPin className="h-4 w-4" />
                    <span>{selectedCity || 'Selecionar cidade'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {userCities.map((city) => (
                    <DropdownMenuItem
                      key={city.id}
                      onClick={() => handleCityChange(city.city)}
                      className="flex items-center gap-2"
                    >
                      <MapPin className="h-4 w-4" />
                      <span>{city.city}</span>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/city-selection')}
                    className="flex items-center gap-2 text-blue-600"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Adicionar nova cidade</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Button 
              variant="ghost" 
              size="sm"
              className="w-full justify-start"
            >
              <Settings className="h-4 w-4 mr-2" />
              <span>Configurações</span>
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
}